import React, { useEffect } from 'react';
import classNames from 'classnames';
import {
  ButtonAppearance,
  ButtonSize,
  IconPosition,
} from '../../../../components/ButtonBase/types';
import { Phrases } from '../../../../utilities/constants/phrases';
import TextEditor from '../../../../components/TextEditor/TextEditor';
import Toolbar from '../../../../components/Toolbar/Toolbar';
import ToolbarControls from '../../../../components/Toolbar/ToolbarControls/ToolbarControls';
import ToolbarRight from '../../../../components/Toolbar/ToolbarRight/ToolbarRight';
import styles from './EditorLayout.module.scss';
import Button from '../../../../components/Button/Button';
import { IconGlyph } from '../../../../components/Icon';
import SubscriptionStatus from '../../../../components/SubscriptionStatus/SubscriptionStatus';
import PremiumAnswerPlaceholder from '../PremiumAnswerPlaceholder/PremiumAnswerPlaceholder';
import { useEditorDownloadCopyText } from '../../../../hooks/editor/useEditorDownloadCopyText';
import FeatureBanner from '../../../../components/FeatureBanner/FeatureBanner';
import SolutionTools from '../../../../components/SolutionTools/SolutionTools';

interface Props {
  isTextGenerating: boolean;
  isPremium: boolean;
  isUserSubscribed: boolean;
  text: string;
  title?: string;
  subjectGroup?: string;
  onAiInputSubmit?: (value: string) => void;
}

const EditorLayout: React.FC<Props> = (props) => {
  const {
    isTextGenerating,
    isPremium,
    isUserSubscribed,
    text,
    title,
    subjectGroup,
    onAiInputSubmit,
  } = props;
  const { onCopyText, onDownloadText } = useEditorDownloadCopyText(text);

  useEffect(() => {
    if (isTextGenerating) {
      window.scrollTo(0, 0);
    }
  }, [isTextGenerating]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.editor}>
        {!isUserSubscribed && (
          <section className={styles.featureBannerWrapper}>
            <FeatureBanner subjectGroup={subjectGroup} />
          </section>
        )}
        <Toolbar>
          <ToolbarControls>
            <Button
              disabled={isTextGenerating || !text}
              onClick={onCopyText}
              appearance={ButtonAppearance.TEXT}
              size={ButtonSize.SMALL}
              icon={{
                position: IconPosition.LEFT,
                glyph: IconGlyph.COPY,
              }}>
              <span className='visually-hidden'>
                {Phrases.COPY_BUTTON_LABEL}
              </span>
            </Button>
            <Button
              disabled={isTextGenerating || !text}
              onClick={onDownloadText}
              appearance={ButtonAppearance.TEXT}
              size={ButtonSize.SMALL}
              icon={{
                position: IconPosition.LEFT,
                glyph: IconGlyph.DOWNLOAD,
              }}>
              <span className='visually-hidden'>
                {Phrases.DOWNLOAD_BUTTON_LABEL}
              </span>
            </Button>
          </ToolbarControls>
          <ToolbarRight>
            <SubscriptionStatus isPremium={isPremium} />
          </ToolbarRight>
        </Toolbar>
        <TextEditor
          text={text}
          isTextGenerating={isTextGenerating}
          isPremium={isPremium}
          subjectGroup={subjectGroup}
          onAiInputSubmit={onAiInputSubmit}
        />
        {!isPremium && <PremiumAnswerPlaceholder title={title} />}
        <section
          className={classNames(styles.solutionToolsWrapper, 'mt-xl mt-md@md')}>
          <SolutionTools subjectGroup={subjectGroup} />
        </section>
      </div>
    </div>
  );
};

export default EditorLayout;
